/**
 * **未来一个月现金流覆盖分析**（批次 `130` · J 域首页 · 负责人 2026-10-31 第 7 条）
 *
 * ## 它回答什么问题
 *
 * 「我账上现在能动的钱，够不够付掉未来 30 天必须付的那些？」
 * —— 以及不够的话，**是哪天开始不够、差多少**。
 *
 * ## 🔴 它**不**回答什么（这是本文件最重要的一段）
 *
 * | 不做 | 为什么 |
 * |---|---|
 * | **不预测未来消费** | K 域 PRD §4.6.3 明文：**跨窗口预测禁止**（"下月会花多少"要用户设条件）。用"近 3 月月均支出"外推未来属于这一类 ⇒ 绝不做 |
 * | **不猜收入** | 系统里**没有**未来的收入数据（周期规则只能绑内部待缴账户、工资只在上账后才被识别）。凭空假设"本月还会有工资入账"就是在编账 |
 * | **不出"你该少花点"这类建议** | `AC-K4.16`：无数字支撑就只出**陈述**、不出**动作** |
 *
 * ⇒ 本函数的输入只有两样，**都是已经确定的事实**：
 * ① 账户里现有的钱（`liquidCents`，可用现金口径）；
 * ② 未来 30 天**已经确定的支付义务**（`lines`，来自 F 域唯一必付聚合）。
 *
 * 输出是**纯算术**：逐日累计、找最低点、算缺口。**没有任何一个数字是估的。**
 *
 * ## 口径细节（三条，都会影响结果）
 *
 * 1. **逾期项归到"今天"** —— `dueDate < today` 的项在账上本来就该付了。
 *    若按原日期算，逐日累计会从一个"过去"的起点开始，最低点会落在已经过去的日子里，
 *    用户会看到一个**无法行动**的结论（"上周三就差了 500"）。归到今天才是可行动的。
 * 2. **缺汇率的项不计入**（`baseCents === null`）—— 与 `composeUpcoming()` 同一纪律：
 *    缺汇率不静默、不按 1:1 冒充。`fxIncomplete` 透传出去让 UI 标注"这个分析不完整"。
 * 3. **最低点并列取最早的一天** —— 同样低的两天，先发生的那天才是用户该准备钱的日子。
 */

import type { Cents } from '../types/brand.js';
import { sumCents, toCents } from './money.js';

/** 覆盖分析的**三态**（🔴 三态穷举 —— 端侧按 `kind` 分支，不自己判"是不是负数"） */
export const CashCoverageKind = {
  /** 窗口内**没有任何必付** ⇒ 无话可说，显示"未来 30 天没有固定支出" */
  NoUpcoming: 'noUpcoming',
  /** 够付：任一时刻余额都 ≥ 0 */
  Sufficient: 'sufficient',
  /** 不够付：某个时刻余额会变成负数（**不截断为 0** —— 要告诉用户差多少） */
  Shortfall: 'shortfall',
} as const;
export type CashCoverageKind = (typeof CashCoverageKind)[keyof typeof CashCoverageKind];

/** 一行必付（**只需这两样** —— 本分析不关心它是什么类型、挂在哪个账户） */
export interface CashCoverageLine {
  readonly id: string;
  /** 到期日 `YYYY-MM-DD`（账本本地日） */
  readonly dueDate: string;
  /**
   * 本币金额（整数分）；`null` = **缺汇率** ⇒ 不计入合计与累计
   * （与 `composeUpcoming()` 的 `baseCents` 同语义）
   */
  readonly baseCents: number | null;
}

export interface ComposeCashCoverageInput {
  /** 账本本地今天 `YYYY-MM-DD` */
  readonly today: string;
  /** **可用现金**（本币整数分，已折算）—— 口径见 `liquidOf()` */
  readonly liquidCents: number;
  readonly lines: readonly CashCoverageLine[];
  /** 上游聚合是否已有缺汇率项 */
  readonly fxIncomplete: boolean;
}

/** 逐日累计的一个点（只含**有必付的那些天** —— 没有支付的日子余额不变，不必出点） */
export interface CashCoveragePoint {
  readonly day: string;
  /** 那天付完之后还剩多少（可为负） */
  readonly remainingCents: Cents;
}

export interface CashCoverageResult {
  readonly kind: CashCoverageKind;
  readonly liquidCents: Cents;
  /** 窗口内必付合计（**只累加可折算的**；全缺汇率时可能为 0 而 `fxIncomplete = true`） */
  readonly upcomingTotalCents: Cents;
  /** 余额**最低**的那一刻（可为负） */
  readonly lowestCents: Cents;
  /** 最低点是哪天；`kind = noUpcoming` 时 = `today` */
  readonly lowestDay: string;
  /** 缺口（**正数**）；`kind = shortfall` 时 > 0，其余为 0 */
  readonly shortfallCents: Cents;
  /** 逐日累计（按日升序） */
  readonly points: readonly CashCoveragePoint[];
  readonly fxIncomplete: boolean;
}

/**
 * 主函数（**唯一实现**）。
 *
 * 🔴 不变量：`lowestCents === min(liquid - 累计已付)`，且 `shortfallCents === max(0, -lowestCents)`。
 * 端侧**不得**自己再累一遍（否则会出现"卡面说差 500、展开明细加起来差 480"）。
 */
export function composeCashCoverage(input: ComposeCashCoverageInput): CashCoverageResult {
  const liquid = toCents(input.liquidCents);

  // ① 只留能折算到本币的（缺汇率的项不计入 —— 与 `composeUpcoming()` 同一条纪律）
  const usable = input.lines.filter(
    (l): l is CashCoverageLine & { baseCents: number } => l.baseCents !== null,
  );
  const total = sumCents(usable.map((l) => toCents(l.baseCents)));

  if (usable.length === 0) {
    return {
      kind: CashCoverageKind.NoUpcoming,
      liquidCents: liquid,
      upcomingTotalCents: toCents(0),
      lowestCents: liquid,
      lowestDay: input.today,
      shortfallCents: toCents(0),
      points: [],
      fxIncomplete: input.fxIncomplete,
    };
  }

  // ② 按日聚合。🔴 逾期项归到**今天**（理由见文件头第 1 条）
  const byDay = new Map<string, number>();
  for (const line of usable) {
    const day = line.dueDate < input.today ? input.today : line.dueDate;
    byDay.set(day, (byDay.get(day) ?? 0) + line.baseCents);
  }

  // ③ 逐日累计 + 找最低点（并列**取最早**）
  const days = [...byDay.keys()].sort();
  const points: CashCoveragePoint[] = [];
  let remaining = liquid as number;
  let lowest = liquid as number;
  let lowestDay = input.today;

  for (const day of days) {
    remaining -= byDay.get(day) ?? 0;
    points.push({ day, remainingCents: toCents(remaining) });
    if (remaining < lowest) {
      lowest = remaining;
      lowestDay = day;
    }
  }

  const shortfall = lowest < 0 ? -lowest : 0;

  return {
    kind: shortfall > 0 ? CashCoverageKind.Shortfall : CashCoverageKind.Sufficient,
    liquidCents: liquid,
    upcomingTotalCents: total,
    lowestCents: toCents(lowest),
    lowestDay,
    shortfallCents: toCents(shortfall),
    points,
    fxIncomplete: input.fxIncomplete,
  };
}

// ─────────────────────────── 可用现金口径 ───────────────────────────

/**
 * 「可用现金」的账户品类白名单 —— 🔴 **只有这三类**。
 *
 * | 类型 | 算不算 | 为什么 |
 * |---|---|---|
 * | `cash`（现金） | ✅ | 字面意义的现金 |
 * | `debit`（储蓄卡） | ✅ | 随时可转出 |
 * | `ewallet`（电子钱包） | ✅ | 同上 |
 * | `investment`（投资账户） | ❌ | 那是**还没买入的待投资金**，但同账户下往往挂着持仓；把它算进"能花的钱"会让用户误以为证券账户里的市值随时能取出付账单 |
 * | `internal(prepaid)`（预付卡） | ❌ | 只能在特定商户消费，付不了信用卡账单 |
 * | `credit`（信用卡） | ❌ | 是负债不是现金 |
 * | `loan` / `contra` | ❌ | 负债 / 往来款 |
 *
 * 🔴 **为什么不复用净资产里的「货币资金」桶**：那个桶（`nucleus/networth.ts` 的 `monetary`）
 * 把 `cash` / `debit` / `ewallet` / **`investment`** / **预付卡** 合成了一类 ——
 * 它是**资产分类**口径（"我有多少货币性资产"），不是**可用现金**口径
 * （"我下个月能拿什么付账单"）。两者混用会把证券账户余额算成能花的钱。
 * ⇒ 本文件另立一份**显式白名单**，且**逐条写明为什么**（上面那张表）。
 */
export const LIQUID_ACCOUNT_TYPES = ['cash', 'debit', 'ewallet'] as const;
export type LiquidAccountType = (typeof LIQUID_ACCOUNT_TYPES)[number];

/** 某个账户的余额是否可计入可用现金（🔴 唯一判据，服务端只调它） */
export function isLiquidAccount(type: string): type is LiquidAccountType {
  return (LIQUID_ACCOUNT_TYPES as readonly string[]).includes(type);
}
